import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, IMG, PageHeader } from "./ui";
import { SITE, breadcrumb, faqJsonLd } from "./seo";

/* ─── A small inline check mark (stroke: currentColor) ────────────────────── */
function Check({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`h-3 w-3 ${className}`} fill="none" stroke="currentColor" strokeWidth="3">
      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── Duration tiers ──────────────────────────────────────────────────────── */
const TIERS: { minutes: number; price: string; use: string; popular?: boolean }[] = [
  { minutes: 15, price: "$17.00", use: "A quick check-in or follow-up on a single issue." },
  { minutes: 30, price: "$34.00", use: "The balanced default for most consultations.", popular: true },
  { minutes: 45, price: "$50.50", use: "Complex, multi-issue visits with several reports." },
  { minutes: 60, price: "$67.50", use: "A thorough, in-depth consultation." },
];

function Tiers() {
  return (
    <section className="py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>Duration sets the price</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Pick a length. See the exact price.
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-warm-muted">
            Consult duration is configurable from 5 to 90 minutes (30 is the default). These are our standard
            rates — we quote the exact price before a visit is booked, and lock it the moment it is.
          </p>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TIERS.map((t) => (
            <div
              key={t.minutes}
              className={`relative flex flex-col rounded-3xl border bg-warm-card p-7 ${
                t.popular
                  ? "border-warm-accent ring-2 ring-warm-accent/30 shadow-[0_24px_60px_-30px_rgba(222,122,69,0.55)]"
                  : "border-warm-border"
              }`}
            >
              {t.popular && (
                <span className="absolute -top-3 left-7 inline-flex items-center rounded-full bg-warm-accent px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-white">
                  Most popular
                </span>
              )}
              <div className="text-sm font-semibold uppercase tracking-[0.14em] text-warm-accent">
                {t.minutes} min
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-bold tracking-tight text-warm-ink">{t.price}</span>
                <span className="text-sm text-warm-muted">/ consult</span>
              </div>
              <p className="mt-4 text-[15px] leading-relaxed text-warm-muted">{t.use}</p>
              <div className="mt-6 flex-1" />
              <MButton
                to="/signup"
                variant={t.popular ? "primary" : "outline"}
                className="w-full"
              >
                Start free <ArrowRight />
              </MButton>
            </div>
          ))}
        </div>
        <p className="mt-8 text-[13px] text-warm-muted">
          One transparent price per consultation — there are no seats, no subscription, and no per-user fees.
        </p>
      </Container>
    </section>
  );
}

/* ─── Everything included ─────────────────────────────────────────────────── */
const INCLUDED: { title: string; body: string }[] = [
  {
    title: "AI video doctor",
    body: "A real-time video consultation that adapts to each patient and runs in the browser.",
  },
  {
    title: "Report ingestion & brief",
    body: "Labs, imaging and notes are extracted and summarized into a pre-visit clinical brief.",
  },
  {
    title: "SOAP visit summary",
    body: "A structured visit summary, patient-friendly explanation, triage and recommended next steps.",
  },
  {
    title: "All patient emails",
    body: "Booking, reminders, secure links and the visit summary — every message is sent for you.",
  },
  {
    title: "Health journal & vitals",
    body: "Each visit updates the patient's longitudinal record, including any vitals captured.",
  },
  {
    title: "Clinician sign-off",
    body: "Route visits to a human review-and-sign queue when your clinical model requires it.",
  },
];

function Included() {
  return (
    <section className="bg-warm-sand/40 py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>One price covers everything</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Every price includes the whole visit.
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-warm-muted">
            There are no add-ons to buy. A single transparent price covers the consultation end to end.
          </p>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {INCLUDED.map((f) => (
            <div key={f.title} className="rounded-3xl border border-warm-border bg-warm-card p-7">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-warm-accent/15 text-warm-accent">
                <Check />
              </span>
              <h3 className="mt-4 font-semibold text-warm-ink">{f.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-warm-muted">{f.body}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── How billing works ───────────────────────────────────────────────────── */
const STEPS: { title: string; body: string }[] = [
  {
    title: "Add a card once",
    body: "Securely capture a card with Stripe. Nothing is charged at capture — it just lives on file.",
  },
  {
    title: "Quote before you book",
    body: "We show you the exact, duration-based price before a consultation is booked.",
  },
  {
    title: "Price locked at booking",
    body: "The moment a visit is booked, that price is locked — it will never drift on you.",
  },
  {
    title: "Billed only when complete",
    body: "You pay only when a consultation is completed. No-shows and cancelled visits are never billed.",
  },
];

function Billing() {
  return (
    <section className="py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>How billing works</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            No surprises, by design.
          </h2>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="rounded-3xl border border-warm-border bg-warm-card p-7">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-warm-ink text-sm font-bold text-warm-bg">
                {i + 1}
              </div>
              <h3 className="mt-4 font-semibold text-warm-ink">{s.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-warm-muted">{s.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-[13px] text-warm-muted">
          Manage your card, watch month-to-date usage and download invoices any time in the Stripe customer portal.
        </p>
      </Container>
    </section>
  );
}

/* ─── Reassurance band ────────────────────────────────────────────────────── */
function Reassurance() {
  return (
    <section className="py-12">
      <Container>
        <div className="flex flex-col items-center gap-6 rounded-4xl bg-warm-ink px-8 py-14 text-center text-warm-bg">
          <h2 className="max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl">
            No seats. No subscription.{" "}
            <span className="font-display italic font-normal text-warm-glow">Pay only for completed consultations.</span>
          </h2>
          <p className="max-w-xl text-warm-bg/70">
            New clinics can run free consultations with a promo code before adding a card. Percent-off coupons are
            supported, and volume rates are available — just reach out.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <MButton to="/signup" className="px-6 py-3 text-base">
              Start free <ArrowRight />
            </MButton>
            <MButton href={`mailto:${SITE.email}`} variant="outline" className="px-6 py-3 text-base">
              Talk to sales
            </MButton>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ─── FAQ ─────────────────────────────────────────────────────────────────── */
const FAQS: { q: string; a: string }[] = [
  {
    q: "When am I billed?",
    a: "Only when a consultation is completed. You add a card once with no charge at capture, and we charge the locked, duration-based price after the visit finishes and the summary is generated.",
  },
  {
    q: "Do I need a subscription?",
    a: "No. WonderfulDoctor is pay-as-you-go. There are no seats, no monthly subscription and no per-user fees — you pay one transparent price per completed consultation.",
  },
  {
    q: "What if a patient no-shows or cancels?",
    a: "You are not billed. No-shows and cancelled visits never generate a charge — billing only happens for consultations that complete.",
  },
  {
    q: "Can I try it for free?",
    a: "Yes. New clinics can run free consultations using promo codes or coupons before adding a card. Paid consultations require a card on file when a visit is booked.",
  },
  {
    q: "How is the price decided?",
    a: "By the consultation's duration, which is configurable from 5 to 90 minutes (30 by default). We quote the exact price before a visit is booked, and lock it the moment it is.",
  },
  {
    q: "Does insurance work with this?",
    a: "Insurance claims and adjudication are out of scope today — you pay per consultation directly. Volume rates and percent-off coupons are available; contact us and we'll set you up.",
  },
];

function Faq() {
  return (
    <section className="bg-warm-sand/40 py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Straight answers about money.
          </h2>
        </div>
        <div className="mt-14 grid gap-4 lg:grid-cols-2">
          {FAQS.map((f) => (
            <details
              key={f.q}
              className="group rounded-3xl border border-warm-border bg-warm-card p-6 [&_summary]:list-none"
            >
              <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold text-warm-ink">
                {f.q}
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-warm-border text-warm-muted transition-transform group-open:rotate-45">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                  </svg>
                </span>
              </summary>
              <p className="mt-3 text-[15px] leading-relaxed text-warm-muted">{f.a}</p>
            </details>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── Closing CTA over imagery ────────────────────────────────────────────── */
function ClosingCta() {
  return (
    <section className="py-24">
      <Container>
        <div className="relative overflow-hidden rounded-4xl border border-warm-border">
          <img
            src={IMG.pricing}
            alt="A clinic administrator and physician reviewing transparent consultation pricing"
            className="h-[360px] w-full object-cover sm:h-[420px]"
          />
          <div className="absolute inset-0 bg-warm-ink/65" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center text-warm-bg">
            <h2 className="max-w-2xl text-balance text-4xl font-bold tracking-tight sm:text-5xl">
              One transparent price that{" "}
              <span className="font-display italic font-normal text-warm-glow">covers the whole visit.</span>
            </h2>
            <p className="mt-5 max-w-xl text-warm-bg/80">
              Start free, quote any consultation in seconds, and pay only for the ones that complete.
            </p>
            <div className="mt-8">
              <MButton to="/signup" className="px-6 py-3 text-base">
                Start free <ArrowRight />
              </MButton>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

export function Pricing() {
  return (
    <MarketingPage
      seo={{
        title: "Pricing — WonderfulDoctor",
        description:
          "Pay-as-you-go AI consultations priced by duration and billed only when complete. No seats, no subscription — one transparent price covers every visit end to end.",
        path: "/pricing",
        jsonLd: [
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Pricing", path: "/pricing" },
          ]),
          faqJsonLd(FAQS),
        ],
      }}
    >
      <PageHeader
        eyebrow="PRICING"
        title="Priced by the minute,"
        accent="billed when complete."
        sub="No seats, no subscription, no per-user fees. You add a card once, we quote the exact price before a visit is booked, and you pay only for consultations that finish."
      >
        <MButton to="/signup" className="px-6 py-3 text-base">
          Start free <ArrowRight />
        </MButton>
        <MButton to="/platform" variant="outline" className="px-6 py-3 text-base">
          See how it works
        </MButton>
      </PageHeader>
      <Tiers />
      <Included />
      <Billing />
      <Reassurance />
      <Faq />
      <ClosingCta />
    </MarketingPage>
  );
}
